import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ActivateAccountSchema } from '@portal/schemas'
import type { z } from 'zod'
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react'
import { authApi } from '@/lib/api/auth'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

type FormValues = Pick<z.infer<typeof ActivateAccountSchema>, 'password'> & { confirmPassword: string }

export default function ActivatePage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [showPass, setShowPass] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { register, handleSubmit, watch } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { password: '', confirmPassword: '' },
  })

  const password = watch('password')

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B2545] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>This activation link is missing a token. Please request a new one from your admin.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const onSubmit = async (values: FormValues) => {
    if (values.password !== values.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setError('')
    setLoading(true)
    try {
      const res = await authApi.activate(token, values.password)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Activation failed')

      const data = res.data
      setAuth(data.user!, data.accessToken!, data.refreshToken!, data.modules ?? [])
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setError(err?.error ?? err?.message ?? 'Activation failed. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  const checks = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
  ]

  return (
    <div className="min-h-screen bg-[#0B2545] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8 gap-3">
          <img src="/logo.png" alt="Monarch Innovation" className="h-12 w-auto" />
          <p className="text-slate-400 text-sm">Set your password to activate your account</p>
        </div>

        <Card className="shadow-2xl border-white/10 bg-white">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Activate Account</CardTitle>
            <CardDescription>Create a secure password for your account</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPass ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pr-10"
                    {...register('password', {
                      minLength: { value: 8, message: 'Min 8 characters' },
                      pattern: { value: /[A-Z]/, message: 'Need uppercase' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Password strength checklist */}
                {password.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {checks.map((c) => (
                      <li key={c.label} className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                        <CheckCircle2 className={`h-3 w-3 ${c.ok ? 'text-emerald-500' : 'text-muted-foreground/40'}`} />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pr-10"
                    {...register('confirmPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-2"
                disabled={loading || checks.some((c) => !c.ok)}
              >
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Activating…</> : 'Activate Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
